import React, { useCallback, useEffect, useState } from 'react';
import { get } from 'lodash';
import { useFieldArray, useWatch } from 'react-hook-form';
import {
  ATMTable,
  ATMField,
  ATMButton,
  ATMGrid,
  ATMInput,
  ATMSegment,
  ATMSelect,
} from 'shared-it-appmod-ui';
import { SettingKey } from 'src/constants';
import Lang from 'src/libraries/language';
import {
  IRequestFormMethods,
  IRequestCNDWONumber,
} from 'src/models/request.model';
import { useSetting } from 'src/hooks/setting.hook';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import { actionTypes as cndDistrictActionTypes } from 'src/ducks/cnd-district.duck';
import { useCNDDistrictContext } from 'src/contexts/cnd-districts.context';
import { getCNDDistrictsStatus } from 'src/selectors/cnd-district.selector';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';

type IProps = IRequestFormMethods & {
  category: string;
};

type WorkOrderValidationState = 'valid' | 'invalid' | 'duplicate';

const cndFieldWidthStyle = { width: '100%', maxWidth: '420px' };

const FlightRequestDroneSCGCND: React.FC<IProps> = ({
  category,
  control,
  setError,
  clearErrors,
  formState,
  errors,
}) => {
  const { fields, append, remove } = useFieldArray<IRequestCNDWONumber>({
    control,
    name: 'droneFlightSDGE.cndWONumbers', // `${category}.cndWONumbers`,
  });
  const maxfield = useSetting(SettingKey.MaxCNDWONumbers, 0);
  const { state: cndDistrictState, actions: cndDistrictActions } = useCNDDistrictContext();

  const [isConfirm, setIsConfirm] = useState<Partial<IRequestCNDWONumber>>();
  // const [isValidIO, setIsValidIO] = useState<Record<string, boolean>>({});
  const [workOrderStatus, setWorkOrderStatus] = useState<
    Record<string, WorkOrderValidationState>
  >({});
  const watchedWONumbers: Array<Partial<IRequestCNDWONumber>> =
    useWatch({
      control,
      name: 'droneFlightSDGE.cndWONumbers',
    }) || [];

  const cndStatus = getCNDDistrictsStatus(
    cndDistrictState,
    cndDistrictActionTypes.CND_DISTRICT_LIST_FETCH
  );

  useEffect(() => {
    if (cndDistrictActions) {
      cndDistrictActions.listGET();
    }
  }, [cndDistrictActions]);

  const handleAdd = useCallback(() => {
    append({ cndWONumber: undefined });
  }, [append]);

  useEffect(() => {
    if (
      !fields.length &&
      get(control, 'defaultValuesRef.current.droneFlightSDGE.cndWONumbers', [])
        .length === 0
    ) {
      handleAdd();
    }
  }, [control, fields, category, handleAdd]);

  useEffect(() => {
    Object.entries(workOrderStatus).forEach(([id, status]) => {
      const index = fields.findIndex((item) => item.id === id);

      if (index === -1) {
        return;
      }

      if (status === 'valid' && clearErrors) {
        clearErrors([`droneFlightSDGE.cndWONumbers[${index}].cndWONumber`]);
      } else if (setError) {
        setError(`droneFlightSDGE.cndWONumbers[${index}].cndWONumber`, {
          type: status,
          message:
            status === 'duplicate'
              ? Lang.MSG_ERROR_WORK_ORDER_DUPLICATE
              : Lang.MSG_ERROR_WORK_ORDER_NOT_VALID,
        });
      }
    });

    if (!clearErrors) {
      return;
    }

    fields.forEach((field, index) => {
      if (workOrderStatus[field.id]) {
        return;
      }

      const fieldPath = `droneFlightSDGE.cndWONumbers[${index}].cndWONumber`;
      const fieldError = get(errors, fieldPath);

      if (fieldError?.type === 'duplicate' || fieldError?.type === 'invalid') {
        clearErrors([fieldPath]);
      }
    });
  }, [
    formState?.isSubmitting,
    fields,
    workOrderStatus,
    clearErrors,
    setError,
    errors,
  ]);

  useEffect(() => {
    const duplicateCounts = watchedWONumbers.reduce((counts, item) => {
      const value = item?.cndWONumber?.trim();

      if (!value) {
        return counts;
      }

      return {
        ...counts,
        [value]: (counts[value] || 0) + 1,
      };
    }, {} as Record<string, number>);

    setWorkOrderStatus((values) => {
      const nextValues = {
        ...values,
      };

      fields.forEach((field, index) => {
        const value = watchedWONumbers[index]?.cndWONumber?.trim();

        if (!value) {
          if (nextValues[field.id] === 'duplicate') {
            delete nextValues[field.id];
          }
          return;
        }

        if (duplicateCounts[value] > 1) {
          nextValues[field.id] = 'duplicate';
        } else if (nextValues[field.id] === 'duplicate') {
          delete nextValues[field.id];
        }
      });

      const sameKeysLength =
        Object.keys(nextValues).length === Object.keys(values).length;
      const isSame =
        sameKeysLength &&
        Object.keys(nextValues).every((key) => nextValues[key] === values[key]);

      if (isSame) {
        return values;
      }

      return nextValues;
    });
  }, [fields, watchedWONumbers]);

  useEffect(() => {
    // On submit, drop blank WO rows from the form state when at least one
    // valid WO exists. This keeps the UI list clean after Save clicks.
    const hasAnyValue = watchedWONumbers.some(
      (item) => !!String(item?.cndWONumber || '').trim()
    );

    if (!formState?.isSubmitting || !hasAnyValue) {
      return;
    }

    const emptyIndices = watchedWONumbers
      .map((item, index) =>
        String(item?.cndWONumber || '').trim() ? -1 : index
      )
      .filter((index) => index !== -1)
      .sort((a, b) => b - a);

    if (!emptyIndices.length) {
      return;
    }

    remove(emptyIndices);
    setWorkOrderStatus({});
  }, [formState?.isSubmitting, watchedWONumbers, remove]);

  const verifyWorkOrder = useCallback(
    (id: string, index: number, value: string) => {
      const normalizedValue = value?.trim() ?? '';

      if (!normalizedValue) {
        setWorkOrderStatus((values) => {
          const nextValues = {
            ...values,
          };

          delete nextValues[id];

          return nextValues;
        });
        if (clearErrors) {
          clearErrors([`droneFlightSDGE.cndWONumbers[${index}].cndWONumber`]);
        }
        return;
      }

      if (normalizedValue.length > 12) {
        setWorkOrderStatus((values) => ({
          ...values,
          [id]: 'invalid',
        }));
        return;
      }

      // Check duplicates
      const duplicates = watchedWONumbers.filter(
        (item, i2) =>
          item?.cndWONumber?.trim() === normalizedValue && i2 !== index
      );

      if (duplicates.length > 0) {
        setWorkOrderStatus((values) => ({
          ...values,
          [id]: 'duplicate',
        }));
        return;
      }

      // Validate with API
      // const response = await requestActions.verifyWorkOrderGET(value);
      // const ok = response?.payload?.valid ?? false;

      // if (!ok) {
      //   setWorkOrderStatus((values) => ({
      //     ...values,
      //     [id]: 'invalid',
      //   }));
      //   return;
      // }

      // Valid
      setWorkOrderStatus((values) => ({
        ...values,
        [id]: 'valid',
      }));
    },
    [watchedWONumbers, clearErrors]
  );

  const handleRemove = useCallback(
    (index) => {
      const data = fields[index];
      const fieldId = data?.id;

      if (data && data.cndWOId) {
        setIsConfirm(data);
      } else {
        if (fieldId) {
          setWorkOrderStatus((values) => {
            const nextValues = {
              ...values,
            };

            delete nextValues[fieldId];

            return nextValues;
          });
        }
        remove(index);
      }
    },
    [fields, remove]
  );

  return (
    <>
      <ATMSegment>
        <ATMGrid columns={1}>
          <ATMGrid.Column style={cndFieldWidthStyle}>
            <ATMField
              name="droneFlightSDGE.cndDistrict"
              label={<LabelRequired>{Lang.LBL_CND_DISTRICTS}</LabelRequired>}
              as={ATMSelect}
              selectOnBlur={false}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={(cndDistrictState.list ?? []).map((value) => ({
                key: value.cndDistrictId,
                value: value.cndDistrict,
                text: value.cndDistrict,
              }))}
              search
              error={errors.droneFlightSDGE?.cndDistrict}
              onChange={([_, { value }]) => value}
              loading={!cndDistrictState.list.length && cndStatus.fetching}
              disabled={!cndDistrictState.list.length && cndStatus.fetching}
            />
          </ATMGrid.Column>
        </ATMGrid>

        <ATMGrid columns={1}>
          <ATMGrid.Column style={cndFieldWidthStyle}>
            <ATMTable style={{ width: '100%' }}>
              <ATMTable.Header>
                <ATMTable.Row>
                  <ATMTable.HeaderCell colSpan={2}>
                    <LabelRequired>{Lang.LBL_CND_WO_NUMBER}</LabelRequired>
                  </ATMTable.HeaderCell>
                </ATMTable.Row>
              </ATMTable.Header>

              <ATMTable.Body>
                {fields.map((item, i) => (
                  <ATMTable.Row key={item.id}>
                    <ATMTable.Cell>
                      {item.cndWOId && (
                        <ATMField
                          key={`cndWOId_droneFlightSDGE_${item.id}`}
                          name={`droneFlightSDGE.cndWONumbers[${i}].cndWOId`}
                          defaultValue={item.cndWOId}
                          className="hidden"
                          as={ATMInput}
                          control={control}
                          type="hidden"
                        />
                      )}
                      <ATMField
                        key={`cndWONumber_${item.id}`}
                        autoComplete="off"
                        as={ATMInput}
                        name={`droneFlightSDGE.cndWONumbers[${i}].cndWONumber`}
                        control={control}
                        maxLength={12}
                        type="text"
                        icon={
                          workOrderStatus[item.id ?? ''] === 'valid' &&
                          'check green'
                        }
                        placeholder={Lang.LBL_ENTER}
                        error={get(
                          errors,
                          `droneFlightSDGE.cndWONumbers[${i}].cndWONumber`,
                          false
                        )}
                        onBlur={(_, value) => {
                          verifyWorkOrder(item.id, i, value);
                        }}
                        // loading={
                        //   getRequestStatus(
                        //     requestState,
                        //     actionTypes.REQUEST_VERIFY_WORK_ORDER
                        //   ).fetching
                        // }
                      />
                    </ATMTable.Cell>
                    <ATMTable.Cell textAlign="right" collapsing>
                      {fields.length > 1 && (
                        <ATMButton
                          icon="trash alternate outline"
                          type="button"
                          onClick={() => handleRemove(i)}
                        />
                      )}
                    </ATMTable.Cell>
                  </ATMTable.Row>
                ))}
              </ATMTable.Body>
              <ATMTable.Footer>
                <ATMTable.Row>
                  <ATMTable.HeaderCell colSpan={4}>
                    <ATMButton
                      secondary
                      icon="plus"
                      type="button"
                      disabled={fields.length >= parseInt(maxfield, 10)}
                      onClick={handleAdd}
                      content={Lang.LBL_ADD}
                    />
                  </ATMTable.HeaderCell>
                </ATMTable.Row>
              </ATMTable.Footer>
            </ATMTable>

            <Confirm
              open={!!isConfirm}
              size="tiny"
              onCancel={() => setIsConfirm(undefined)}
              header={Lang.TTL_CONFIRM_DELETE}
              content={Lang.formatString(
                Lang.MSG_CONFIRM_DELETE,
                isConfirm?.cndWONumber ?? ''
              )}
              onConfirm={() => {
                if (isConfirm) {
                  const index = fields.findIndex(
                    (value) => value.cndWOId === isConfirm.cndWOId
                  );

                  if (index !== -1) {
                    const fieldId = fields[index]?.id;

                    if (fieldId) {
                      setWorkOrderStatus((values) => {
                        const nextValues = {
                          ...values,
                        };

                        delete nextValues[fieldId];

                        return nextValues;
                      });
                    }
                    remove(index);
                  }
                  setIsConfirm(undefined);
                }
              }}
            />
          </ATMGrid.Column>
        </ATMGrid>
      </ATMSegment>
    </>
  );
};

export default FlightRequestDroneSCGCND;
